/**
 * @lines98/client/lib/gnome-state — модель состояния 7 гномов + чистые-функции
 * раскрытия бонусов (мета «Гномы», `meta-gnomes.md` §2/§4.1).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §3 «модуль-логика → lib/», §4 no-hardcode).
 * Карточка гнома (`components/gnome-card`) и плашка «Гномы» (`screens/gnomes-screen`)
 * рисуют СОСТОЯНИЕ гнома (уровень 1..20 / число престижей / какие бонусы открыты).
 * Эта логика «открыт ли бонус на уровне L» и «прогресс к следующему порогу» — ОДНА на
 * проект (карточка + деталь будут читать её), поэтому живёт в модуле, не копируется по
 * экранам. Пороги (`GNOME_*_UNLOCK`) и макс-уровень — из shared-config (один источник,
 * клиент=сервер), НЕ литералы (AC-Gn16 / I-Gn5).
 *
 * **UI-FIRST (GN-1/2/6, owner-approved 2026-06-13): server-блок `gnomes` ещё НЕ
 * реализован (GN-4).** До него карточки рендерятся из ПЛЕЙСХОЛДЕР-модели (`PLACEHOLDER_
 * GNOMES` / `demoGnomes`) — фикс-раскладка 7 гномов «как у нового игрока» (все L1) или
 * демо-микс для скриншотов. Форма `GnomeState` совпадает с будущей server-проекцией
 * (`§4.1`: color / level / prestige_count) → когда GN-4 начнёт слать блок `gnomes`,
 * экран подменит источник без переписывания карточки (server-truth, I-Gn4 — как
 * Мастерская). НИКАКОЙ бонус-логики/множителей/faucet тут НЕТ (GN-5, после balance) —
 * только презентация состояния.
 *
 * SCOPE: тип `GnomeState`, чистые предикаты раскрытия бонусов (`isGoldUnlocked` и т.д.),
 * прогресс к следующему порогу, placeholder/demo-билдеры. NOT IN SCOPE: множители добычи
 * (GN-5), мутации апгрейд/престиж (GN-4 server), цены/кривые (balance).
 *
 * Cross-references:
 *   - meta-gnomes.md §2.1/§2.2/§2.4 (модель гнома / раскрытие бонусов / престиж), §4.1
 *     (server-проекция), §4.2 (маппинг цвет↔гном), §5.1 (карточка), §11 GN-1/GN-2.
 *   - @lines98/shared/config/meta (GNOME_MAX_LEVEL / GNOME_*_UNLOCK — single source).
 *   - components/gnome-card.ts (consumer: рисует состояние), screens/gnomes-screen.ts.
 */

import {
  GNOME_GEM_CURVE,
  GNOME_GEM_UNLOCK,
  GNOME_GOLD_CURVE,
  GNOME_GOLD_UNLOCK,
  GNOME_MAX_LEVEL,
  GNOME_PRESTIGE_GEM_PER,
  GNOME_PRESTIGE_GOLD_PER,
  GNOME_PRESTIGE_SHARDS_PER,
  GNOME_SHARDS_CURVE,
  GNOME_SHARDS_UNLOCK,
  gnomeContribution,
  gnomeUpgradeCost as gnomeUpgradeCostConfig,
  type GnomeUpgradeCost,
} from '@lines98/shared/config/meta';
import type { ColorIndex } from '@lines98/shared/types/game';

import { t } from './i18n/index.js';

/**
 * Стоимость апгрейда гнома L→L+1 (золото + осколки) — единый client-helper (П12 AC-12.R1).
 * Re-export чистой config-функции (`@lines98/shared/config/meta`): UI (`gnome-detail`) зовёт
 * ЕЁ, не лезет в config-таблицы напрямую и не хардкодит цены (no-hardcode, single source).
 * Индексируется ТЕКУЩИМ уровнем (цена перехода L→L+1; `[20]`=0 — на максимуме «Престиж»).
 */
export function gnomeUpgradeCost(level: number): GnomeUpgradeCost {
  return gnomeUpgradeCostConfig(level);
}
export type { GnomeUpgradeCost };

/** Число гномов = число цветов движка (`meta-gnomes.md` §2.1). */
export const GNOME_COUNT = 7 as const;

/**
 * Состояние одного гнома (проекция; форма совпадает с будущей server-проекцией §4.1).
 * Стабильный ключ — `color` (= индекс движка = арт-индекс). Накопленный перм-бонус
 * престижа — производное от `prestigeCount` (§4.1: «можно вычислять, чтобы не хранить») →
 * в UI-проекции храним только счётчик.
 */
export interface GnomeState {
  /** colorIndex 1..7 — стабильный ключ (цвет ↔ гном ↔ его камень, §4.2). */
  readonly color: ColorIndex;
  /** Уровень 1..GNOME_MAX_LEVEL. */
  readonly level: number;
  /** Число престижей (≥ 0, без потолка, §2.4). */
  readonly prestigeCount: number;
  /**
   * Открыт ли гном (GN-2b / rev 0.3 §2.0). owned-set стартует ПУСТ → у нового игрока
   * все 7 = `false`. Server-truth (`gnomes[].unlocked` из `GET /meta/state`, I-Gn4):
   * нельзя купить/форсить (I-Gn10) — только доиграть до уровня-вехи. locked-слот всё
   * равно занимает место в раскладке (7 слотов всегда, AC-1) — рисуется силуэтом
   * (карточка ветвит `applyState` на это поле). Единый source-of-truth для карточки/
   * детали/экрана: они читают `isUnlocked`, не дублируют предикат (AC-10).
   */
  readonly unlocked: boolean;
}

/**
 * Имя гнома = имя его ЦВЕТА (рабочие подписи; финал RU/оттенки — owner/art, §10 O-5).
 * i18n-ключ цвета по индексу 1..7 → `t('game.color.<name>')` (локализуемо на 15 языков;
 * gap-extract wave 7). Индекс 0 — sentinel (нет цвета).
 */
const GNOME_COLOR_KEY_BY_INDEX: readonly string[] = [
  '', // [0] sentinel
  'game.color.red', // 1
  'game.color.green', // 2
  'game.color.blue', // 3
  'game.color.yellow', // 4
  'game.color.pink', // 5
  'game.color.cyan', // 6
  'game.color.orange', // 7
] as const;

/**
 * Локализованное имя гнома по его цвету (резолвится по АКТИВНОЙ локали в момент вызова —
 * экран ремонтируется на смену языка). Неизвестный цвет → fallback «Помощник {n}».
 */
export function gnomeName(color: ColorIndex): string {
  const key = GNOME_COLOR_KEY_BY_INDEX[color];
  return key ? t(key) : t('game.gnomeCard.nameFallback', { n: color });
}

/** Категория бонуса гнома (раскрывается по уровню, §2.2). */
export type GnomeBonusKind = 'gold' | 'shards' | 'gem';

/** Открыт ли бонус ЗОЛОТА (с L1, §2.2 / I-Gn5). */
export function isGoldUnlocked(level: number): boolean {
  return level >= GNOME_GOLD_UNLOCK;
}

/** Открыт ли бонус ОСКОЛКОВ (с L5, §2.2 / I-Gn5). */
export function isShardsUnlocked(level: number): boolean {
  return level >= GNOME_SHARDS_UNLOCK;
}

/** Открыт ли бонус КАМНЯ своего цвета (с L15, §2.2 / I-Gn5). */
export function isGemUnlocked(level: number): boolean {
  return level >= GNOME_GEM_UNLOCK;
}

/** Уровень-порог раскрытия категории бонуса (config single-source, §4.5). */
export function unlockLevelFor(kind: GnomeBonusKind): number {
  switch (kind) {
    case 'gold':
      return GNOME_GOLD_UNLOCK;
    case 'shards':
      return GNOME_SHARDS_UNLOCK;
    case 'gem':
      return GNOME_GEM_UNLOCK;
  }
}

/** Открыта ли категория на данном уровне. */
export function isBonusUnlocked(kind: GnomeBonusKind, level: number): boolean {
  return level >= unlockLevelFor(kind);
}

/** Гном на максимуме уровня → действие = «Престиж», не «Прокачать» (§2.4 / edge-case). */
export function isMaxLevel(level: number): boolean {
  return level >= GNOME_MAX_LEVEL;
}

// =============================================================================
// Открытие гномов (GN-2b / rev 0.3 §2.0) — owned-set, server-truth, diff для reveal.
// Single source логики «открыт ли гном» + «какие открылись между ресинками» (AC-10):
// карточка / деталь / экран читают ЭТИ функции, не дублируют предикат по файлам.
// =============================================================================

/**
 * Открыт ли гном (предикат single source, AC-10). Карточка/деталь/экран ветвят на него,
 * не на сырое поле напрямую — одна точка истины, если правило усложнится (нет).
 */
export function isUnlocked(g: GnomeState): boolean {
  return g.unlocked === true;
}

/**
 * Уровень СЛЕДУЮЩЕЙ вехи открытия для строки «следующий гном на уровне N» (AC-3).
 *
 * **NO-HARDCODE (AC-16 / I-Gn10):** это **passthrough server-truth** — число вехи приходит
 * с сервера (`next_unlock_level = GNOME_UNLOCK_LEVELS[gnome_unlocks_granted]`, считает
 * сервер). Клиент НЕ хранит и НЕ угадывает массив вех `[2,6,11,17,23,27,31]` — отдаёт ровно
 * то, что прислал сервер. Отсутствие/`null` (все 7 открыты, AC-19) → `null` → строка
 * скрыта. Чистая функция (нормализует undefined→null; не «угадывает» первую веху при
 * отсутствии данных — иначе UI врал бы про порог).
 *
 * @param serverValue — `next_unlock_level` из `GET /meta/state` (число / null / undefined).
 * @returns уровень-веха для строки, или `null` если строку надо скрыть.
 */
export function nextUnlockLevel(serverValue: number | null | undefined): number | null {
  return typeof serverValue === 'number' && Number.isFinite(serverValue) ? serverValue : null;
}

/**
 * Diff «что открылось между двумя ресинками» — триггер reveal-очереди (AC-17/AC-18).
 * Возвращает цвета гномов, которые стали `unlocked:true` в `next`, будучи locked в `prev`,
 * **в порядке возрастания цвета** (= порядок индексов вех, как сервер выдал, §5.1 → очередь
 * reveal детерминирована). Монотонность (§2.0): открытие необратимо, обратный переход
 * (open→locked) НЕ учитывается (клиент не форсит закрытие). Ничего не изменилось → `[]`
 * (reveal не запускается, I-Gn10). Чистая функция (юнит-тестируема).
 *
 * @param prev — предыдущий снимок 7 гномов (или подмножество по color).
 * @param next — новый снимок 7 гномов (server-truth после ресинка).
 * @returns цвета новооткрытых гномов по возрастанию.
 */
export function newlyUnlockedColors(
  prev: readonly GnomeState[],
  next: readonly GnomeState[],
): ColorIndex[] {
  // Множество уже-открытых ДО ресинка — по стабильному ключу color (порядок prev не важен).
  const wasUnlocked = new Set<number>();
  for (const g of prev) if (isUnlocked(g)) wasUnlocked.add(g.color);

  const newly: ColorIndex[] = [];
  for (const g of next) {
    if (isUnlocked(g) && !wasUnlocked.has(g.color)) newly.push(g.color);
  }
  // Порядок очереди reveal = возрастание цвета (= индекса вехи, §5.1) — детерминизм.
  newly.sort((a, b) => a - b);
  return newly;
}

// =============================================================================
// Бонус-процент по ресурсу (карточка «иконка + +X%», owner-эскиз 2026-06-14).
// =============================================================================

/**
 * Config-пакет одного ресурса гнома (кривая уровня + порог открытия + перм-дельта
 * престижа) — единый источник для показа бонуса. Все три — из shared-config (no-hardcode,
 * §4); ровно те же значения, что читает серверная добыча (`gnomes-state.ts`).
 */
interface BonusResourceConfig {
  readonly curve: readonly number[];
  readonly unlock: number;
  readonly per: number;
}

/** Маппинг ресурс → config-пакет (curve/unlock/per). */
function bonusConfigFor(kind: GnomeBonusKind): BonusResourceConfig {
  switch (kind) {
    case 'gold':
      return { curve: GNOME_GOLD_CURVE, unlock: GNOME_GOLD_UNLOCK, per: GNOME_PRESTIGE_GOLD_PER };
    case 'shards':
      return {
        curve: GNOME_SHARDS_CURVE,
        unlock: GNOME_SHARDS_UNLOCK,
        per: GNOME_PRESTIGE_SHARDS_PER,
      };
    case 'gem':
      return { curve: GNOME_GEM_CURVE, unlock: GNOME_GEM_UNLOCK, per: GNOME_PRESTIGE_GEM_PER };
  }
}

/**
 * Бонус-процент по ресурсу для показа на карточке «иконка + +X.X%» И в детали (owner-эскиз
 * 2026-06-14). Возвращает **ДРОБНЫЙ процент** (НЕ округлённый — рост дробей виден, в т.ч.
 * камень <1%) или `null` когда бонус ещё ЗАКРЫТ (level < порога) — карточка/деталь тогда
 * рисуют «откроется на L?».
 *
 * **Показ = серверная истина (AC-1/AC-2):** зовём ТУ ЖЕ `gnomeContribution` из shared-config,
 * что минтит серверная добыча (`gnomes-state.ts`): `levelChance(level, curve, unlock) +
 * prestigeCount × prestigePer`. Значение растёт с уровнем (по config-кривой) и с престижем
 * (перм-дельта) — клиент=сервер байт-в-байт (до форматирования). Возвращаем долю × 100
 * (проценты), БЕЗ `Math.round` (owner-директива точности: показываем десятые доли «+0.1%»…
 * «+8.0%», иначе камень всегда «+0%» и рост невидим). Формат-строку «+X.X%» с фикс-точностью
 * делает `formatGnomeBonusPercent` (1 знак). Чистая функция (юнит-тестируема, проекция-only —
 * НЕ вход в трату/начисление, I-Gn1).
 */
export function gnomeBonusPercent(kind: GnomeBonusKind, state: GnomeState): number | null {
  if (!isBonusUnlocked(kind, state.level)) return null;
  const { curve, unlock, per } = bonusConfigFor(kind);
  const prestige = Math.max(0, Math.floor(state.prestigeCount));
  // Доля [0,1+] из ЕДИНОЙ shared-формулы (зеркало серверной добычи) → проценты ×100.
  // БЕЗ округления — десятые доли обязаны быть видны (owner-директива точности).
  return gnomeContribution(state.level, prestige, curve, unlock, per) * 100;
}

/**
 * Число знаков после разделителя в показе бонуса гнома «+X.X%». **= 1** (owner-директива
 * точности 2026-06-22): ровно 1 десятичный знак, НИКОГДА не округлять до целого. Трасса:
 * минимальный рост, который обязан быть виден — камень L15→L20 = 0.1%→0.4% (шаг 0.1%);
 * золото L1 = 0.5%. 1 знак различает все эти шаги; 0 знаков свёл бы камень к «+0%» (рост
 * невидим — баг, который чинит эта фича). Больше 1 знака не нужно (кривые кратны 0.1% в
 * показе; перм-дельта камня 0.5% — тоже 1 знак). Константа, не magic-number в форматтере.
 */
export const GNOME_BONUS_PERCENT_FRACTION_DIGITS = 1 as const;

/**
 * Формат показа бонуса гнома «+X.X%» (фикс-точность). ЕДИНЫЙ хелпер для карточки
 * (`bonus-pill.setOpen`) И детали (`applyBonusRow`) — один источник формата, не копия по
 * компонентам (code-structure §3). Десятичный разделитель — ТОЧКА (owner-directive
 * 2026-06-23: «+0.5%», не запятая) → `toFixed` (всегда точка, вне локали). Ровно
 * `GNOME_BONUS_PERCENT_FRACTION_DIGITS` знак(а) после разделителя; знак «+» впереди.
 *
 * @param percent — дробный процент (выход `gnomeBonusPercent`, доля×100).
 * @returns строка вида «+0.5%» / «+8.0%» / «+0.1%».
 */
export function formatGnomeBonusPercent(percent: number): string {
  const digits = GNOME_BONUS_PERCENT_FRACTION_DIGITS;
  const body = percent.toFixed(digits);
  return `+${body}%`;
}

/**
 * Доля прогресса уровня [0..1] для полосы-прогресса карточки (§5.1). L1 → 0,
 * L20 → 1. Чистая функция (юнит-тестируема). Зажата к [0,1] на случай битого уровня.
 */
export function levelProgress(level: number): number {
  const span = GNOME_MAX_LEVEL - 1;
  if (span <= 0) return 1;
  if (!Number.isFinite(level)) return 0; // битый уровень → 0 (не NaN в width-стиле)
  const p = (level - 1) / span;
  return Math.max(0, Math.min(1, p));
}

/**
 * Плейсхолдер-состояние НОВОГО игрока: все 7 гномов LOCKED (owned-set ПУСТ, rev 0.3 §2.0 /
 * AC-1). Используется плашкой «Гномы» мгновенно до прихода server-блока `gnomes` (GN-4):
 * 7 locked-слотов видны сразу (силуэты), раскладка не «прыгает» при reveal. _(rev 0.3
 * отменяет прежний «все 7 на L1 открыты»: гном открывается только за уровень-веху, I-Gn10.)_
 */
export const PLACEHOLDER_GNOMES: readonly GnomeState[] = buildGnomes(() => ({
  level: 1,
  prestigeCount: 0,
  unlocked: false,
}));

/**
 * Собрать массив 7 гномов из per-color билдера (хелпер для placeholder/demo + достройки
 * server-блока, AC-12). Цвета 1..7 по порядку → стабильная раскладка (§4.2). Недостающие
 * у сервера записи трактуются билдером как locked (вызывающий ставит `unlocked:false`).
 */
export function buildGnomes(
  perColor: (color: ColorIndex) => { level: number; prestigeCount: number; unlocked: boolean },
): GnomeState[] {
  const out: GnomeState[] = [];
  for (let c = 1; c <= GNOME_COUNT; c++) {
    const color = c as ColorIndex;
    const { level, prestigeCount, unlocked } = perColor(color);
    out.push({
      color,
      level: clampLevel(level),
      prestigeCount: Math.max(0, Math.floor(prestigeCount)),
      unlocked: unlocked === true,
    });
  }
  return out;
}

/** Зажать уровень в допустимый диапазон 1..GNOME_MAX_LEVEL (defensive). */
export function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return 1;
  return Math.max(1, Math.min(GNOME_MAX_LEVEL, Math.floor(level)));
}

/**
 * Демо-микс состояний для скриншот-baseline (§0.D) — РАЗНЫЕ стадии ОТКРЫТЫХ гномов
 * (карточка показывает все ветки: L1/L5/L8/L15/L20/престижи). Все 7 ОТКРЫТЫ (legacy
 * `?demo=gnomes&state=mix` — экран открытых карточек до GN-2b). НЕ server-truth.
 */
export function demoGnomes(): GnomeState[] {
  // color → (level, prestigeCount). Покрывает: новый (L1), порог осколков (L5), середина
  // (L8), порог камня (L15), максимум (L20 → «Престиж»), престижи (бейдж). Все unlocked.
  const mix: Record<number, { level: number; prestigeCount: number }> = {
    1: { level: 20, prestigeCount: 2 }, // red — максимум + 2 престижа (кнопка «Престиж» + бейдж)
    2: { level: 1, prestigeCount: 0 }, // green — новый (только золото)
    3: { level: 8, prestigeCount: 0 }, // blue — золото + осколки открыты
    4: { level: 5, prestigeCount: 0 }, // yellow — ровно порог осколков
    5: { level: 15, prestigeCount: 1 }, // pink — порог камня + 1 престиж
    6: { level: 3, prestigeCount: 0 }, // cyan — золото открыто, осколки/камень спят
    7: { level: 17, prestigeCount: 0 }, // orange — все три бонуса открыты
  };
  return buildGnomes((color) => ({
    ...(mix[color] ?? { level: 1, prestigeCount: 0 }),
    unlocked: true,
  }));
}

/**
 * Демо-микс PARTIAL (GN-2b `?demo=gnomes&state=partial`): цвета 1 и 3 ОТКРЫТЫ, остальные 5
 * — locked-силуэты. Совпадает с server-mock partial-теста (openColors=[1,3]) → детерминизм
 * baseline locked+открыт рядом. НЕ server-truth.
 */
export function demoGnomesPartial(): GnomeState[] {
  const open: Record<number, { level: number; prestigeCount: number }> = {
    1: { level: 8, prestigeCount: 0 }, // red — золото + осколки открыты
    3: { level: 6, prestigeCount: 0 }, // blue — золото открыт
  };
  return buildGnomes((color) => {
    const o = open[color];
    return o ? { ...o, unlocked: true } : { level: 1, prestigeCount: 0, unlocked: false };
  });
}
